import React, { useEffect, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { Template } from '../types';
import { Modal } from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Toast, ToastMessage } from '../components/Toast';
import { Mail, Plus, Trash2, Edit2, Copy, PlusCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';

const INITIAL_TEMPLATE: Partial<Template> = {
  title: '',
  content: '',
  language: 'cs',
  tags: []
};

export default function Templates() {
  const { wedding, locale } = useWedding();
  const t = (key: string) => getTranslation(locale, key);

  const [templates, setTemplates] = useState<Template[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Template>>(INITIAL_TEMPLATE);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchTemplates = async () => {
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    const { data } = await supabase.from('templates').select('*').eq('wedding_id', wedding.id);
    if (data) setTemplates(data);
  };

  useEffect(() => {
    fetchTemplates();
  }, [wedding]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    if (formData.id) {
      const { error } = await supabase.from('templates').update(formData).eq('id', formData.id);
      if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    } else {
      const { error } = await supabase.from('templates').insert({ ...formData, wedding_id: wedding.id });
      if (error) { showToast('error', `Chyba: ${error.message}`); return; }
    }
    showToast('success', t('common.save'));
    setIsModalOpen(false);
    fetchTemplates();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('templates').delete().eq('id', deleteId);
    setDeleteId(null);
    if (error) showToast('error', `Chyba: ${error.message}`);
    else fetchTemplates();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Zkopírováno do schránky!');
  };

  if (!wedding) return null;

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('nav.templates')}</h1>
          <p className="text-ds-body text-slate-500 dark:text-slate-400 mt-2">Šablony e-mailů a zpráv pro hosty.</p>
        </div>
        <Button 
          onClick={() => { setFormData(INITIAL_TEMPLATE); setIsModalOpen(true); }}
          variant="secondary"
          icon={<PlusCircle size={18} />}
        >
          {t('common.add')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.length === 0 ? (
          <div className="col-span-full">
            <EmptyState 
              icon={Mail} 
              title="Žádné šablony" 
              description="Vytvořte si šablonu pro pozvánky, připomínky nebo poděkování."
              action={{ label: t('common.add'), onClick: () => { setFormData(INITIAL_TEMPLATE); setIsModalOpen(true); } }}
            />
          </div>
        ) : (
          templates.map(tpl => (
            <div key={tpl.id} className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-ds-border transition-all group relative flex flex-col h-full">
               <div className="flex justify-between items-start mb-4">
                  <span className="text-ds-meta text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                     {tpl.language.toUpperCase()}
                  </span>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                     <button onClick={() => copyToClipboard(tpl.content)} className="p-1.5 text-slate-400 hover:text-brand-600" title="Zkopírovat"><Copy size={16} /></button>
                     <button onClick={() => { setFormData(tpl); setIsModalOpen(true); }} className="p-1.5 text-slate-400 hover:text-brand-600"><Edit2 size={16} /></button>
                     <button onClick={() => setDeleteId(tpl.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
               </div>
               
               <h3 className="text-ds-h3 font-semibold text-slate-900 dark:text-white mb-2">{tpl.title}</h3>
               <div className="flex-1 bg-slate-50 dark:bg-slate-950 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
                  <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-6 whitespace-pre-wrap font-mono">{tpl.content}</p>
               </div>
            </div>
          ))
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={formData.id ? 'Upravit šablonu' : 'Nová šablona'}>
         <form onSubmit={handleSave} className="space-y-6">
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">Název šablony</label>
               <input 
                  type="text" 
                  required
                  value={formData.title || ''} 
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder="Např. Pozvánka - verze 1"
               />
            </div>
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">Obsah zprávy</label>
               <textarea 
                  required
                  value={formData.content || ''} 
                  onChange={e => setFormData({ ...formData, content: e.target.value })}
                  rows={8}
                  className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500 custom-scrollbar font-mono text-sm"
               />
            </div>
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">Jazyk</label>
               <select 
                  value={formData.language} 
                  onChange={e => setFormData({...formData, language: e.target.value as any})}
                  className="w-full p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 outline-none focus:ring-2 focus:ring-brand-500"
               >
                  <option value="cs">Čeština</option>
                  <option value="en">English</option>
               </select>
            </div>
            <button type="submit" className="w-full bg-brand-600 text-white font-bold py-4 rounded-xl hover:bg-brand-700 transition-colors">
               {t('common.save')}
            </button>
         </form>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onCancel={() => setDeleteId(null)} onConfirm={handleDelete} title={t('common.delete')} description={t('common.confirm_delete')} />

      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}